import React from 'react'
import styled from 'styled-components'

import Image from '@/components/image'
import RichText, {hasRichText} from '@/components/rich-text'
import {Text} from '@/components/text'
import {BackgroundWrapper, Layout, Section} from '@/styles/grid'
import {checkmarkList} from '@/styles/lists'
import mq from '@/utils/media-query'

import type {Props as RichTextProps} from '@/components/rich-text'
import type {BackgroundWrapperProps} from '@/styles/grid'
import type {ImageSource} from '@/types/image'

interface Item {
    image?: ImageSource
    title: string
    body?: RichTextProps['content']
    bodyExtra?: RichTextProps['content']
}

export interface Props {
    title?: string
    items: Item[]
    backgroundColor?: BackgroundWrapperProps['$color']
    dataTestId?: string
}

const TitleWithFeatures = React.forwardRef<HTMLDivElement, Props>(
    ({title, items, backgroundColor, dataTestId}, ref) => {
        return (
            <BackgroundWrapper ref={ref} $color={backgroundColor} data-testid={dataTestId}>
                <Wrapper>
                    {title && (
                        <Layout $variant="two-asymmetrical">
                            <Text $variant="heading-large" as="h2">
                                {title}
                            </Text>
                        </Layout>
                    )}

                    <Layout
                        $variant="three-equal"
                        {...(dataTestId && {'data-testid': `${dataTestId}-card-wrapper`})}
                    >
                        {items.map((item, i) => (
                            <ItemWrapper key={`${item.title}-${i}`}>
                                <ImageTitleWrapper>
                                    {item.image && <Image image={item.image} width={380} />}

                                    <Text $variant="title-base">{item.title}</Text>
                                </ImageTitleWrapper>

                                {hasRichText(item.body) && (
                                    <RichText $bgColor="black" content={item.body} />
                                )}

                                {hasRichText(item.bodyExtra) && (
                                    <>
                                        <hr />

                                        <RichText $bgColor="black" content={item.bodyExtra} />
                                    </>
                                )}
                            </ItemWrapper>
                        ))}
                    </Layout>
                </Wrapper>
            </BackgroundWrapper>
        )
    },
)

export default TitleWithFeatures

const Wrapper = styled(Section)`
    display: grid;
    gap: ${({theme}) => theme.spacings.s20};

    ${mq.min('sm')} {
        gap: ${({theme}) => theme.spacings.s60};
    }
`

const ItemWrapper = styled.div`
    display: grid;
    row-gap: ${({theme}) => theme.spacings.s16};

    ul {
        ${checkmarkList('black')}
    }

    hr {
        border-color: ${({theme}) => theme.colors.border.secondary};
    }
`

const ImageTitleWrapper = styled.div`
    display: grid;
    gap: ${({theme}) => theme.spacings.s20};

    ${mq.min('sm')} {
        gap: ${({theme}) => theme.spacings.s40};
    }

    img {
        width: 100%;
        max-height: 250px;
        object-fit: contain;
    }
`
